import { getPeriodicElement } from './periodicTableData';

export type AtomicModelsMode = 'models' | 'isotopes';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const ATOMIC_MODELS_TOOL_IDENTITY = {
  id: 'atomic-models',
  toolKey: 'chemistry-atomic-models',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const ATOMIC_MODELS_REVIEW_STATUS = 'pending-teaching-review' as const;

/**
 * Default mode by Year (CT-MODE). Both modes are available from Year 9; the isotope view becomes
 * the default from Year 11, when the senior relative-atomic-mass section is open by default.
 */
export const ATOMIC_MODES: readonly { mode: AtomicModelsMode; fromYear: number }[] = [
  { mode: 'models', fromYear: 9 },
  { mode: 'isotopes', fromYear: 11 },
];

/** First Year at which the senior relative-atomic-mass section opens by default. */
export const SENIOR_ABUNDANCE_FROM_YEAR = 11;

/** Science tool that owns the radioactive decay model. This tool links to it and never copies it. */
export const SCIENCE_HALF_LIFE_TOOL_PATH =
  '/learn/study/subjects/science/tools/radiometric-dating-half-life';

export const ATOMIC_MODEL_SOURCES = [
  'J. Dalton, A New System of Chemical Philosophy (1808); atomic theory first presented 1803',
  'J. J. Thomson, "Cathode Rays", Philosophical Magazine 44 (1897) and the 1904 atom model',
  'H. Geiger and E. Marsden, Proc. R. Soc. A 82 (1909); E. Rutherford, Philosophical Magazine 21 (1911)',
  'N. Bohr, "On the Constitution of Atoms and Molecules", Philosophical Magazine 26 (1913)',
  'J. Chadwick, "Existence of a Neutron", Proc. R. Soc. A 136 (1932)',
] as const;

export const ISOTOPE_DATA_SOURCES = [
  'IUPAC/CIAAW, Isotopic compositions of the elements 2013 (Pure Appl. Chem. 88, 293–306, 2016) — representative isotopic abundances',
  'AME2020 atomic mass evaluation (Chinese Physics C 45, 030003, 2021) — isotopic masses in u',
  'IUPAC/CIAAW standard atomic weights — periodic table comparison values',
] as const;

// ---------------------------------------------------------------------------------------------
// Atomic model timeline
// ---------------------------------------------------------------------------------------------

export type ModelStepId = 'dalton' | 'thomson' | 'rutherford' | 'bohr' | 'chadwick';

export interface AtomicModelStep {
  id: ModelStepId;
  scientist: string;
  yearLabel: string;
  modelName: string;
  picture: string;
  evidence: string;
  explained: string;
  couldNotExplain: string;
  /** What the deterministic schematic draws for this step. */
  schematic: {
    nucleus: 'none' | 'diffuse-positive' | 'small-dense';
    electrons: 'none' | 'embedded' | 'cloud' | 'shells';
    neutrons: boolean;
  };
}

export const ATOMIC_MODEL_STEPS: AtomicModelStep[] = [
  {
    id: 'dalton',
    scientist: 'John Dalton',
    yearLabel: '1803',
    modelName: 'Solid sphere model',
    picture: 'Atoms are tiny, solid, indivisible spheres. Atoms of one element are identical.',
    evidence:
      'Elements combine in fixed mass ratios (law of definite proportions) and in simple whole-number ratios (law of multiple proportions).',
    explained: 'Why compounds always have the same composition by mass.',
    couldNotExplain:
      'Electricity and charged particles inside atoms, and atoms of one element with different masses (isotopes).',
    schematic: { nucleus: 'none', electrons: 'none', neutrons: false },
  },
  {
    id: 'thomson',
    scientist: 'J. J. Thomson',
    yearLabel: '1897 / 1904',
    modelName: 'Plum pudding model',
    picture:
      'A sphere of spread-out positive charge with small negative electrons embedded through it.',
    evidence:
      'Cathode rays were bent by electric and magnetic fields. They were made of the same tiny negative particle (the electron), whatever metal was used.',
    explained: 'Atoms contain electrons, so atoms are not indivisible.',
    couldNotExplain:
      'Why a few alpha particles bounced back from thin gold foil. Spread-out positive charge is too weak to turn them around.',
    schematic: { nucleus: 'diffuse-positive', electrons: 'embedded', neutrons: false },
  },
  {
    id: 'rutherford',
    scientist: 'Ernest Rutherford (with Geiger and Marsden)',
    yearLabel: '1909–1911',
    modelName: 'Nuclear model',
    picture:
      'A tiny, dense, positive nucleus holds almost all the mass. Electrons move in the mostly empty space around it.',
    evidence:
      'In the gold foil experiment most alpha particles passed straight through, a few were deflected and about 1 in 8000 bounced back.',
    explained: 'Large-angle scattering: only a small, concentrated positive charge can repel an alpha particle that strongly.',
    couldNotExplain:
      'Why moving electrons do not lose energy and spiral into the nucleus, and why hydrogen gives off only certain colours of light.',
    schematic: { nucleus: 'small-dense', electrons: 'cloud', neutrons: false },
  },
  {
    id: 'bohr',
    scientist: 'Niels Bohr',
    yearLabel: '1913',
    modelName: 'Shell (energy level) model',
    picture:
      'Electrons occupy fixed energy levels (shells). An electron absorbs or emits a fixed amount of energy when it moves between levels.',
    evidence:
      'The hydrogen emission spectrum is a set of separate lines, not a continuous rainbow, so only certain electron energies are allowed.',
    explained: 'Line spectra and why atoms are stable.',
    couldNotExplain:
      'Why the nucleus has about twice the mass expected from its protons, and the detailed spectra of atoms with many electrons.',
    schematic: { nucleus: 'small-dense', electrons: 'shells', neutrons: false },
  },
  {
    id: 'chadwick',
    scientist: 'James Chadwick',
    yearLabel: '1932',
    modelName: 'Nucleus of protons and neutrons',
    picture:
      'The nucleus contains positive protons and neutral neutrons of almost equal mass. Electrons occupy the space around it.',
    evidence:
      'Beryllium hit by alpha particles gave off neutral radiation that knocked protons out of paraffin wax. Only a neutral particle with about the mass of a proton fitted the results.',
    explained: 'The extra mass of nuclei, and isotopes: same number of protons, different numbers of neutrons.',
    couldNotExplain:
      'Exactly where electrons are. Later quantum models describe electrons with orbitals (regions of probability).',
    schematic: { nucleus: 'small-dense', electrons: 'shells', neutrons: true },
  },
];

// ---------------------------------------------------------------------------------------------
// Prediction questions (models mode)
// ---------------------------------------------------------------------------------------------

export interface PredictionOption {
  id: string;
  label: string;
  /** Specific chemistry feedback when the learner picks this option. */
  feedback: string;
}

export interface ModelQuestion {
  id: string;
  stepId: ModelStepId;
  prompt: string;
  options: PredictionOption[];
  correctOptionId: string;
}

export interface PredictionResult {
  correct: boolean;
  feedback: string;
}

export const GOLD_FOIL_OUTCOME_LABELS = {
  'all-straight': 'Almost all pass straight through with only tiny deflections',
  'most-straight-few-back': 'Most pass straight through, a few deflect and very few bounce back',
  'many-back': 'Many bounce straight back',
  'all-stopped': 'All are stopped by the foil',
} as const;

export type GoldFoilOutcome = keyof typeof GOLD_FOIL_OUTCOME_LABELS;

const goldFoilOptions = (feedback: Record<GoldFoilOutcome, string>): PredictionOption[] =>
  (Object.keys(GOLD_FOIL_OUTCOME_LABELS) as GoldFoilOutcome[]).map((id) => ({
    id,
    label: GOLD_FOIL_OUTCOME_LABELS[id],
    feedback: feedback[id],
  }));

/** Gold foil step 1: what the plum-pudding model predicts. */
export const PLUM_PUDDING_PREDICTION: ModelQuestion = {
  id: 'gold-foil-plum-pudding',
  stepId: 'thomson',
  prompt:
    'If Thomson’s plum pudding model were right, what should alpha particles do when they hit thin gold foil?',
  correctOptionId: 'all-straight',
  options: goldFoilOptions({
    'all-straight':
      'Yes. Positive charge spread thinly through the atom gives only weak pushes, so fast alpha particles should barely change direction.',
    'most-straight-few-back':
      'That is what was later observed, but it is not what the plum pudding model predicts. Spread-out charge cannot turn an alpha particle around.',
    'many-back':
      'No. Bouncing back needs a strong, concentrated repulsion. The plum pudding model has no concentrated positive charge.',
    'all-stopped':
      'No. The foil is very thin (about 1000 atoms thick) and alpha particles are fast and energetic, so the model expects them to pass through.',
  }),
};

/** Gold foil step 2: what Geiger and Marsden actually observed. */
export const GOLD_FOIL_OBSERVATION: ModelQuestion = {
  id: 'gold-foil-observed',
  stepId: 'rutherford',
  prompt: 'Geiger and Marsden ran the experiment. What did they actually observe?',
  correctOptionId: 'most-straight-few-back',
  options: goldFoilOptions({
    'all-straight':
      'This is the plum pudding prediction. The surprise was that a few alpha particles were deflected through large angles, and some bounced back.',
    'most-straight-few-back':
      'Correct. Most passed straight through (mostly empty space), a few were deflected and about 1 in 8000 bounced back (a tiny, dense, positive nucleus).',
    'many-back':
      'Too many. Only about 1 in 8000 bounced back. If many did, the atom would have to be mostly solid.',
    'all-stopped':
      'No. Most alpha particles reached the detector behind the foil, so most of each atom is empty space.',
  }),
};

export const MODEL_QUESTIONS: ModelQuestion[] = [
  {
    id: 'dalton-proportions',
    stepId: 'dalton',
    prompt: 'Water from any source always contains hydrogen and oxygen in a 1 : 8 mass ratio. Which idea did this support?',
    correctOptionId: 'fixed-atoms',
    options: [
      {
        id: 'fixed-atoms',
        label: 'Compounds are made of whole atoms combined in fixed numbers',
        feedback: 'Yes. Whole atoms of fixed mass, joined in fixed numbers, always give the same mass ratio. This was Dalton’s key evidence.',
      },
      {
        id: 'electrons',
        label: 'Atoms contain electrons',
        feedback: 'Not yet. Mass ratios say nothing about charge. Electrons were discovered by Thomson in 1897.',
      },
      {
        id: 'nucleus',
        label: 'Atoms have a tiny nucleus',
        feedback: 'No. The nucleus came from scattering experiments over 100 years later. Mass ratios only show that atoms combine in fixed numbers.',
      },
    ],
  },
  {
    id: 'thomson-cathode',
    stepId: 'thomson',
    prompt: 'Cathode rays bend towards a positive plate, and every metal gives the same particle. What does that show?',
    correctOptionId: 'negative-part',
    options: [
      {
        id: 'negative-part',
        label: 'All atoms contain the same small negative particle',
        feedback: 'Correct. Bending towards a positive plate shows negative charge, and the same particle from every metal shows it is part of all atoms. Dalton’s indivisible atom had to change.',
      },
      {
        id: 'positive-part',
        label: 'Atoms contain a positive particle',
        feedback: 'Check the direction. A particle that moves towards a positive plate is negatively charged.',
      },
      {
        id: 'indivisible',
        label: 'Atoms cannot be split',
        feedback: 'This is the opposite. Pulling a particle out of atoms shows that atoms have smaller parts.',
      },
    ],
  },
  {
    id: 'rutherford-empty',
    stepId: 'rutherford',
    prompt: 'Most alpha particles passed straight through the gold foil. What does that tell you about the atom?',
    correctOptionId: 'empty',
    options: [
      {
        id: 'empty',
        label: 'Most of the atom is empty space',
        feedback: 'Yes. If nothing pushes on most alpha particles, most of each atom contains no concentrated mass or charge.',
      },
      {
        id: 'foil-thin',
        label: 'The gold atoms have gaps between them',
        feedback: 'Gold atoms in the foil are packed tightly. The particles pass through the atoms themselves, so the atoms must be mostly empty.',
      },
      {
        id: 'no-charge',
        label: 'Gold atoms have no charge inside them',
        feedback: 'No. The few large deflections prove there is charge. It is just concentrated in a very small region.',
      },
    ],
  },
  {
    id: 'rutherford-bounce',
    stepId: 'rutherford',
    prompt: 'About 1 in 8000 alpha particles bounced back. What must be inside the atom?',
    correctOptionId: 'dense-positive',
    options: [
      {
        id: 'dense-positive',
        label: 'A tiny, dense, positively charged centre',
        feedback: 'Correct. Positive alpha particles were repelled strongly, so the centre is positive. It was hit rarely, so it is tiny. It turned them around, so it holds a lot of mass.',
      },
      {
        id: 'dense-negative',
        label: 'A tiny, dense, negatively charged centre',
        feedback: 'Alpha particles are positive. A negative centre would attract them, not push them back.',
      },
      {
        id: 'electrons-block',
        label: 'Electrons blocking the way',
        feedback: 'Electrons are about 1/7300 the mass of an alpha particle. They are far too light to bounce it back.',
      },
    ],
  },
  {
    id: 'bohr-spectrum',
    stepId: 'bohr',
    prompt: 'Heated hydrogen gives off only a few separate colours of light. How did Bohr explain this?',
    correctOptionId: 'fixed-levels',
    options: [
      {
        id: 'fixed-levels',
        label: 'Electrons can only have fixed energy levels',
        feedback: 'Yes. An electron dropping between two fixed levels gives off one fixed amount of energy, which is one colour (one spectral line).',
      },
      {
        id: 'any-energy',
        label: 'Electrons can have any amount of energy',
        feedback: 'Any amount of energy would give a continuous rainbow, not separate lines.',
      },
      {
        id: 'nucleus-glows',
        label: 'The nucleus glows',
        feedback: 'Spectral lines come from electrons changing energy level, not from the nucleus.',
      },
    ],
  },
  {
    id: 'bohr-shells',
    stepId: 'bohr',
    prompt: 'A sodium atom has 11 electrons. How are they arranged in Bohr shells?',
    correctOptionId: '2-8-1',
    options: [
      {
        id: '2-8-1',
        label: '2, 8, 1',
        feedback: 'Correct. The first shell holds 2, the second 8, and the last electron starts the third shell.',
      },
      {
        id: '8-2-1',
        label: '8, 2, 1',
        feedback: 'The shell closest to the nucleus fills first, and it holds only 2 electrons.',
      },
      {
        id: '2-9',
        label: '2, 9',
        feedback: 'The second shell holds a maximum of 8 electrons, so the 11th electron goes into the third shell.',
      },
    ],
  },
  {
    id: 'chadwick-mass',
    stepId: 'chadwick',
    prompt: 'A helium nucleus has a charge of 2+ but a mass of about 4 u. What explains the extra mass?',
    correctOptionId: 'neutrons',
    options: [
      {
        id: 'neutrons',
        label: 'Two neutral particles with about the mass of a proton',
        feedback: 'Correct. Two protons give the 2+ charge and about 2 u. Two neutrons add about 2 u with no charge.',
      },
      {
        id: 'electrons',
        label: 'Electrons inside the nucleus',
        feedback: 'Electrons are about 1/1836 of a proton’s mass, so they cannot add 2 u. They would also cancel charge.',
      },
      {
        id: 'more-protons',
        label: 'Four protons',
        feedback: 'Four protons would give a 4+ charge. The measured charge is 2+.',
      },
    ],
  },
  {
    id: 'chadwick-detect',
    stepId: 'chadwick',
    prompt: 'Why was the neutron discovered about 20 years after the nucleus?',
    correctOptionId: 'no-charge',
    options: [
      {
        id: 'no-charge',
        label: 'It has no charge, so electric and magnetic fields do not bend it',
        feedback: 'Yes. Detectors of the time sensed charged particles. Chadwick detected neutrons only through the protons they knocked out of wax.',
      },
      {
        id: 'tiny-mass',
        label: 'It is much lighter than an electron',
        feedback: 'No. A neutron has about the same mass as a proton, about 1839 times the mass of an electron.',
      },
      {
        id: 'outside',
        label: 'It is found outside the nucleus',
        feedback: 'Neutrons are in the nucleus, together with protons.',
      },
    ],
  },
  {
    id: 'dalton-isotopes',
    stepId: 'dalton',
    prompt: 'Chlorine atoms have masses of about 35 u and 37 u. Which model first explains this?',
    correctOptionId: 'chadwick',
    options: [
      {
        id: 'chadwick',
        label: 'The proton–neutron nucleus (Chadwick)',
        feedback: 'Correct. Both atoms have 17 protons, so both are chlorine. They have 18 or 20 neutrons, which changes the mass.',
      },
      {
        id: 'dalton',
        label: 'Dalton’s solid spheres',
        feedback: 'Dalton said every atom of one element is identical, so two masses for chlorine contradict his model.',
      },
      {
        id: 'bohr',
        label: 'Bohr’s shells',
        feedback: 'Shells describe electrons. Isotopes have the same electron arrangement. The mass difference is in the nucleus.',
      },
    ],
  },
];

/** Returns null (fail closed) when the option is not part of the reviewed question. */
export function checkPrediction(question: ModelQuestion, optionId: string): PredictionResult | null {
  const option = question.options.find((candidate) => candidate.id === optionId);
  if (!option) return null;
  return { correct: option.id === question.correctOptionId, feedback: option.feedback };
}

// ---------------------------------------------------------------------------------------------
// Gold foil scattering schematic (deterministic, not to scale)
// ---------------------------------------------------------------------------------------------

export type ScatterModel = 'plum-pudding' | 'nuclear';
export type ScatterOutcome = 'straight' | 'deflected' | 'bounced';

export interface ScatterTrack {
  impactParameter: number;
  deflectionDeg: number;
  outcome: ScatterOutcome;
}

/** Impact parameters (fraction of atom radius). Close passes are over-represented so rare events are visible. */
export const GOLD_FOIL_IMPACT_PARAMETERS = [
  -0.92, -0.78, -0.64, -0.5, -0.36, -0.22, -0.09, -0.03, -0.004, 0.0015, 0.012, 0.05, 0.15, 0.28,
  0.42, 0.56, 0.7, 0.84, 0.97,
] as const;

const RUTHERFORD_DISTANCE_SCALE = 0.006;
const PLUM_PUDDING_MAX_DEFLECTION_DEG = 0.8;
const STRAIGHT_LIMIT_DEG = 5;
const BOUNCE_LIMIT_DEG = 90;

/** Observed scale of back-scattering reported by Geiger and Marsden (1909). */
export const REAL_BOUNCE_BACK_RATIO = '1 in 8000';

export function classifyDeflection(deflectionDeg: number): ScatterOutcome {
  const angle = Math.abs(deflectionDeg);
  if (angle < STRAIGHT_LIMIT_DEG) return 'straight';
  if (angle <= BOUNCE_LIMIT_DEG) return 'deflected';
  return 'bounced';
}

/**
 * Nuclear model uses the Rutherford relation tan(θ/2) = d / b. The plum-pudding model gives only
 * small deflections because its positive charge is spread through the whole atom.
 */
export function scatterTracks(model: ScatterModel): ScatterTrack[] {
  return GOLD_FOIL_IMPACT_PARAMETERS.map((impactParameter) => {
    const sign = impactParameter < 0 ? -1 : 1;
    const magnitude = Math.abs(impactParameter);
    const deflectionDeg =
      model === 'nuclear'
        ? sign * ((2 * Math.atan(RUTHERFORD_DISTANCE_SCALE / magnitude) * 180) / Math.PI)
        : sign * PLUM_PUDDING_MAX_DEFLECTION_DEG * (1 - magnitude);
    return { impactParameter, deflectionDeg, outcome: classifyDeflection(deflectionDeg) };
  });
}

export function countOutcomes(tracks: ScatterTrack[]): Record<ScatterOutcome, number> {
  return tracks.reduce<Record<ScatterOutcome, number>>(
    (counts, track) => ({ ...counts, [track.outcome]: counts[track.outcome] + 1 }),
    { straight: 0, deflected: 0, bounced: 0 },
  );
}

// ---------------------------------------------------------------------------------------------
// Atom and ion builder
// ---------------------------------------------------------------------------------------------

export const BUILDER_LIMITS = {
  minProtons: 1,
  maxProtons: 92,
  maxNeutrons: 146,
  maxCharge: 3,
} as const;

/** Bohr shell capacities used for the shell schematic (valid for up to 20 electrons). */
export const SHELL_CAPACITIES = [2, 8, 8, 2] as const;
export const MAX_SHELL_DIAGRAM_ELECTRONS = 20;

export interface AtomCounts {
  protons: number;
  neutrons: number;
  electrons: number;
}

export type BuildResult =
  | {
      ok: true;
      symbol: string;
      name: string;
      atomicNumber: number;
      massNumber: number;
      charge: number;
      chargeLabel: string;
      kind: 'atom' | 'cation' | 'anion';
      reviewedCaseId: string | null;
    }
  | { ok: false; reason: string };

export interface IsotopeCase {
  id: string;
  name: string;
  counts: AtomCounts;
  status: 'stable' | 'radioactive';
  note: string;
}

export const ISOTOPE_CASES: IsotopeCase[] = [
  { id: 'h-1', name: 'Hydrogen-1 (protium)', counts: { protons: 1, neutrons: 0, electrons: 1 }, status: 'stable', note: 'The only common nucleus with no neutrons. About 99.99% of hydrogen.' },
  { id: 'h-2', name: 'Hydrogen-2 (deuterium)', counts: { protons: 1, neutrons: 1, electrons: 1 }, status: 'stable', note: 'Same chemistry as hydrogen-1, but about twice the mass. Used in heavy water.' },
  { id: 'h-3', name: 'Hydrogen-3 (tritium)', counts: { protons: 1, neutrons: 2, electrons: 1 }, status: 'radioactive', note: 'Radioactive. Used in some self-lit exit signs.' },
  { id: 'c-12', name: 'Carbon-12', counts: { protons: 6, neutrons: 6, electrons: 6 }, status: 'stable', note: 'About 98.9% of carbon. Defines the atomic mass unit: exactly 12 u.' },
  { id: 'c-13', name: 'Carbon-13', counts: { protons: 6, neutrons: 7, electrons: 6 }, status: 'stable', note: 'About 1.1% of carbon. Used in NMR spectroscopy.' },
  { id: 'c-14', name: 'Carbon-14', counts: { protons: 6, neutrons: 8, electrons: 6 }, status: 'radioactive', note: 'Radioactive. Used in radiocarbon dating.' },
  { id: 'cl-35', name: 'Chlorine-35', counts: { protons: 17, neutrons: 18, electrons: 17 }, status: 'stable', note: 'About 76% of chlorine.' },
  { id: 'cl-37', name: 'Chlorine-37', counts: { protons: 17, neutrons: 20, electrons: 17 }, status: 'stable', note: 'About 24% of chlorine.' },
  { id: 'u-235', name: 'Uranium-235', counts: { protons: 92, neutrons: 143, electrons: 92 }, status: 'radioactive', note: 'Radioactive. About 0.7% of natural uranium; the isotope used in most nuclear reactors.' },
  { id: 'u-238', name: 'Uranium-238', counts: { protons: 92, neutrons: 146, electrons: 92 }, status: 'radioactive', note: 'Radioactive. About 99.3% of natural uranium.' },
  { id: 'na-ion', name: 'Sodium ion (²³Na⁺)', counts: { protons: 11, neutrons: 12, electrons: 10 }, status: 'stable', note: 'Sodium loses one electron to form Na⁺. The nucleus does not change.' },
  { id: 'o-ion', name: 'Oxide ion (¹⁶O²⁻)', counts: { protons: 8, neutrons: 8, electrons: 10 }, status: 'stable', note: 'Oxygen gains two electrons to form O²⁻.' },
  { id: 'mg-ion', name: 'Magnesium ion (²⁴Mg²⁺)', counts: { protons: 12, neutrons: 12, electrons: 10 }, status: 'stable', note: 'Magnesium loses two electrons to form Mg²⁺.' },
  { id: 'cl-ion', name: 'Chloride ion (³⁵Cl⁻)', counts: { protons: 17, neutrons: 18, electrons: 18 }, status: 'stable', note: 'Chlorine gains one electron to form Cl⁻.' },
];

const SUPERSCRIPT_DIGITS: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
};
const SUBSCRIPT_DIGITS: Record<string, string> = {
  '0': '₀', '1': '₁', '2': '₂', '3': '₃', '4': '₄', '5': '₅', '6': '₆', '7': '₇', '8': '₈', '9': '₉',
};

export const toSuperscript = (value: number) =>
  String(value).split('').map((digit) => SUPERSCRIPT_DIGITS[digit] ?? digit).join('');
export const toSubscript = (value: number) =>
  String(value).split('').map((digit) => SUBSCRIPT_DIGITS[digit] ?? digit).join('');

/** Charge written as a chemist writes it: "", "+", "2+", "−", "3−". */
export function chargeText(charge: number): string {
  if (charge === 0) return '';
  const magnitude = Math.abs(charge) === 1 ? '' : String(Math.abs(charge));
  return `${magnitude}${charge > 0 ? '+' : '−'}`;
}

export function chargeSuperscript(charge: number): string {
  if (charge === 0) return '';
  const magnitude = Math.abs(charge) === 1 ? '' : toSuperscript(Math.abs(charge));
  return `${magnitude}${charge > 0 ? '⁺' : '⁻'}`;
}

const isWhole = (value: number) => Number.isInteger(value) && value >= 0;

/** Builds an atom or ion from particle counts. Fails closed outside the reviewed ranges. */
export function buildAtom(counts: AtomCounts): BuildResult {
  const { protons, neutrons, electrons } = counts;
  if (!isWhole(protons) || !isWhole(neutrons) || !isWhole(electrons)) {
    return { ok: false, reason: 'Particle counts must be whole numbers of zero or more.' };
  }
  if (protons < BUILDER_LIMITS.minProtons) {
    return { ok: false, reason: 'An atom needs at least one proton. With no protons there is no element.' };
  }
  if (protons > BUILDER_LIMITS.maxProtons) {
    return { ok: false, reason: `This builder supports elements 1 to ${BUILDER_LIMITS.maxProtons} (hydrogen to uranium).` };
  }
  if (neutrons > BUILDER_LIMITS.maxNeutrons) {
    return { ok: false, reason: `This builder supports up to ${BUILDER_LIMITS.maxNeutrons} neutrons.` };
  }
  const charge = protons - electrons;
  if (Math.abs(charge) > BUILDER_LIMITS.maxCharge) {
    return {
      ok: false,
      reason: `A charge of ${chargeText(charge)} is outside the reviewed range (3− to 3+). Simple ions gain or lose at most three electrons.`,
    };
  }
  const element = getPeriodicElement(protons);
  if (!element) return { ok: false, reason: 'No reviewed element data for this proton number.' };
  const reviewed = ISOTOPE_CASES.find(
    (candidate) =>
      candidate.counts.protons === protons &&
      candidate.counts.neutrons === neutrons &&
      candidate.counts.electrons === electrons,
  );
  return {
    ok: true,
    symbol: element.symbol,
    name: element.name,
    atomicNumber: protons,
    massNumber: protons + neutrons,
    charge,
    chargeLabel: chargeText(charge),
    kind: charge === 0 ? 'atom' : charge > 0 ? 'cation' : 'anion',
    reviewedCaseId: reviewed?.id ?? null,
  };
}

/** Isotope notation pieces, for example mass 14, number 6, symbol C. */
export function isotopeNotation(result: Extract<BuildResult, { ok: true }>): string {
  return `${toSuperscript(result.massNumber)}${toSubscript(result.atomicNumber)}${result.symbol}${chargeSuperscript(result.charge)}`;
}

/** Fills Bohr shells for the schematic. Returns null above 20 electrons (diagram not supported). */
export function shellArrangement(electrons: number): number[] | null {
  if (!isWhole(electrons) || electrons > MAX_SHELL_DIAGRAM_ELECTRONS) return null;
  const shells: number[] = [];
  let remaining = electrons;
  for (const capacity of SHELL_CAPACITIES) {
    if (remaining <= 0) break;
    const placed = Math.min(capacity, remaining);
    shells.push(placed);
    remaining -= placed;
  }
  return shells;
}

/** Specific feedback when the learner builds a target case. */
export function checkBuildTarget(target: IsotopeCase, counts: AtomCounts): PredictionResult {
  const built = buildAtom(counts);
  const goal = target.counts;
  if (!built.ok) return { correct: false, feedback: built.reason };
  const goalElement = getPeriodicElement(goal.protons);
  const goalMass = goal.protons + goal.neutrons;
  if (counts.protons !== goal.protons) {
    return {
      correct: false,
      feedback: `${counts.protons} protons makes ${built.name}, not ${goalElement?.name ?? 'the target element'}. The proton number (atomic number) decides the element.`,
    };
  }
  if (counts.neutrons !== goal.neutrons) {
    return {
      correct: false,
      feedback: `Right element, wrong isotope. Mass number = protons + neutrons, so you need ${goalMass} − ${goal.protons} = ${goal.neutrons} neutrons, not ${counts.neutrons}.`,
    };
  }
  if (counts.electrons !== goal.electrons) {
    const goalCharge = goal.protons - goal.electrons;
    return {
      correct: false,
      feedback: `The nucleus is right, but the charge is ${chargeText(built.charge) || '0'}. The target charge is ${chargeText(goalCharge) || '0 (neutral)'}, so it needs ${goal.electrons} electrons.`,
    };
  }
  return {
    correct: true,
    feedback: `Correct: ${isotopeNotation(built)} has ${goal.protons} protons, ${goal.neutrons} neutrons and ${goal.electrons} electrons. ${target.note}`,
  };
}

// ---------------------------------------------------------------------------------------------
// Relative atomic mass from isotopic abundances (senior)
// ---------------------------------------------------------------------------------------------

export interface IsotopeAbundance {
  massNumber: number;
  /** Isotopic mass in u (AME2020). */
  isotopicMass: number;
  /** Representative amount fraction (IUPAC/CIAAW 2013). */
  abundance: number;
}

export interface AbundanceElement {
  id: string;
  symbol: string;
  name: string;
  atomicNumber: number;
  isotopes: IsotopeAbundance[];
}

export const ABUNDANCE_ELEMENTS: AbundanceElement[] = [
  { id: 'li', symbol: 'Li', name: 'Lithium', atomicNumber: 3, isotopes: [
    { massNumber: 6, isotopicMass: 6.0151229, abundance: 0.0759 },
    { massNumber: 7, isotopicMass: 7.0160034, abundance: 0.9241 },
  ] },
  { id: 'b', symbol: 'B', name: 'Boron', atomicNumber: 5, isotopes: [
    { massNumber: 10, isotopicMass: 10.0129370, abundance: 0.199 },
    { massNumber: 11, isotopicMass: 11.0093052, abundance: 0.801 },
  ] },
  { id: 'ne', symbol: 'Ne', name: 'Neon', atomicNumber: 10, isotopes: [
    { massNumber: 20, isotopicMass: 19.9924402, abundance: 0.9048 },
    { massNumber: 21, isotopicMass: 20.9938467, abundance: 0.0027 },
    { massNumber: 22, isotopicMass: 21.9913851, abundance: 0.0925 },
  ] },
  { id: 'mg', symbol: 'Mg', name: 'Magnesium', atomicNumber: 12, isotopes: [
    { massNumber: 24, isotopicMass: 23.9850417, abundance: 0.7899 },
    { massNumber: 25, isotopicMass: 24.9858370, abundance: 0.1 },
    { massNumber: 26, isotopicMass: 25.9825930, abundance: 0.1101 },
  ] },
  { id: 'si', symbol: 'Si', name: 'Silicon', atomicNumber: 14, isotopes: [
    { massNumber: 28, isotopicMass: 27.9769265, abundance: 0.92223 },
    { massNumber: 29, isotopicMass: 28.9764947, abundance: 0.04685 },
    { massNumber: 30, isotopicMass: 29.9737701, abundance: 0.03092 },
  ] },
  { id: 'cl', symbol: 'Cl', name: 'Chlorine', atomicNumber: 17, isotopes: [
    { massNumber: 35, isotopicMass: 34.9688527, abundance: 0.7576 },
    { massNumber: 37, isotopicMass: 36.9659026, abundance: 0.2424 },
  ] },
  { id: 'cu', symbol: 'Cu', name: 'Copper', atomicNumber: 29, isotopes: [
    { massNumber: 63, isotopicMass: 62.9295975, abundance: 0.6915 },
    { massNumber: 65, isotopicMass: 64.9277895, abundance: 0.3085 },
  ] },
  { id: 'ga', symbol: 'Ga', name: 'Gallium', atomicNumber: 31, isotopes: [
    { massNumber: 69, isotopicMass: 68.9255736, abundance: 0.60108 },
    { massNumber: 71, isotopicMass: 70.9247013, abundance: 0.39892 },
  ] },
  { id: 'br', symbol: 'Br', name: 'Bromine', atomicNumber: 35, isotopes: [
    { massNumber: 79, isotopicMass: 78.9183376, abundance: 0.5069 },
    { massNumber: 81, isotopicMass: 80.9162906, abundance: 0.4931 },
  ] },
  { id: 'ag', symbol: 'Ag', name: 'Silver', atomicNumber: 47, isotopes: [
    { massNumber: 107, isotopicMass: 106.905097, abundance: 0.51839 },
    { massNumber: 109, isotopicMass: 108.904752, abundance: 0.48161 },
  ] },
];

/** Accepted distance from the abundance-weighted mean, in u. */
export const RAM_TOLERANCE = 0.02;
const ABUNDANCE_SUM_TOLERANCE = 0.0005;

export function abundanceTotal(element: AbundanceElement): number {
  return element.isotopes.reduce((sum, isotope) => sum + isotope.abundance, 0);
}

/** Abundance-weighted mean of isotopic masses. Throws (fails loud) if abundances do not total 1. */
export function relativeAtomicMass(element: AbundanceElement): number {
  if (Math.abs(abundanceTotal(element) - 1) > ABUNDANCE_SUM_TOLERANCE) {
    throw new Error(`Isotopic abundances for ${element.symbol} do not total 100%`);
  }
  return element.isotopes.reduce((sum, isotope) => sum + isotope.isotopicMass * isotope.abundance, 0);
}

export function massNumberWeightedMean(element: AbundanceElement): number {
  return element.isotopes.reduce((sum, isotope) => sum + isotope.massNumber * isotope.abundance, 0);
}

export function simpleMeanOfMasses(element: AbundanceElement): number {
  return element.isotopes.reduce((sum, isotope) => sum + isotope.isotopicMass, 0) / element.isotopes.length;
}

/** Periodic-table value for comparison (standard atomic weight, conventional value). */
export function periodicTableMass(element: AbundanceElement): number | null {
  const value = Number.parseFloat(getPeriodicElement(element.atomicNumber)?.atomicMass ?? '');
  return Number.isFinite(value) ? value : null;
}

export type RamCheck =
  | { status: 'correct' | 'approximate' | 'simple-mean' | 'wrong'; feedback: string; exact: number }
  | { status: 'invalid'; feedback: string };

export function checkRelativeAtomicMass(element: AbundanceElement, answer: number): RamCheck {
  if (!Number.isFinite(answer) || answer <= 0) {
    return { status: 'invalid', feedback: 'Enter a positive number in u, for example 35.45.' };
  }
  const exact = relativeAtomicMass(element);
  const rounded = exact.toFixed(3);
  const heaviest = Math.max(...element.isotopes.map((isotope) => isotope.isotopicMass));
  const lightest = Math.min(...element.isotopes.map((isotope) => isotope.isotopicMass));
  const dominant = [...element.isotopes].sort((a, b) => b.abundance - a.abundance)[0];
  if (Math.abs(answer - exact) <= RAM_TOLERANCE) {
    return {
      status: 'correct',
      exact,
      feedback: `Correct. Ar(${element.symbol}) = Σ(isotopic mass × fraction) = ${rounded}. It sits closest to ${element.symbol}-${dominant.massNumber} because that isotope is the most abundant.`,
    };
  }
  if (Math.abs(answer - massNumberWeightedMean(element)) <= RAM_TOLERANCE) {
    return {
      status: 'approximate',
      exact,
      feedback: `Your weighting is right, but you used mass numbers instead of isotopic masses. That is a good estimate; with isotopic masses the value is ${rounded}.`,
    };
  }
  if (Math.abs(answer - simpleMeanOfMasses(element)) <= RAM_TOLERANCE) {
    return {
      status: 'simple-mean',
      exact,
      feedback: `That is the simple average of the isotope masses. Each isotope must count by its abundance: ${element.symbol}-${dominant.massNumber} makes up ${(dominant.abundance * 100).toFixed(2)}%, so the mean is pulled towards it.`,
    };
  }
  if (answer < lightest || answer > heaviest) {
    return {
      status: 'wrong',
      exact,
      feedback: `A weighted mean must lie between the lightest (${lightest.toFixed(3)}) and heaviest (${heaviest.toFixed(3)}) isotope. Multiply each mass by its fraction, then add.`,
    };
  }
  return {
    status: 'wrong',
    exact,
    feedback: `Not yet. Convert each percentage to a fraction (for example ${(dominant.abundance * 100).toFixed(2)}% → ${dominant.abundance}), multiply by that isotope’s mass, then add the products.`,
  };
}
